import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

// ─────────────────────────────────────────────────────────────────────────────
// One-line filter chip strip with a "+N more" overflow dropdown — shared by the
// Services category strip and the Tasks group strip so both behave identically.
//
// Shows as many chips as fit the available width (re-measured on resize and whenever the
// items change); the rest move into a "+N more ▾" button that opens a searchable dropdown.
// If the active chip is one of the hidden ones, the button shows that chip (highlighted)
// instead of "+N more", so the applied filter is always visible. The first item (e.g.
// "All Categories") is always shown.
//
//   <ChipOverflowStrip
//     items={[{ key, name, count, color? }]}
//     activeKey={...} onSelect={key => ...} T={T} darkMode={darkMode} />
//
// Place it in a `flex:1; min-width:0` box; keep any pinned action (e.g. "+ Add") outside it.
// ─────────────────────────────────────────────────────────────────────────────

const GAP = 6;

function chipStyle(on, T, darkMode) {
  return {
    display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, whiteSpace: 'nowrap', cursor: 'pointer',
    fontSize: 12, fontWeight: on ? 600 : 500, flexShrink: 0,
    border: `1px solid ${on ? '#3b82f6' : T.border2}`,
    background: on ? (darkMode ? 'rgba(59,130,246,0.15)' : '#eff6ff') : 'transparent',
    color: on ? '#3b82f6' : T.text2,
  };
}

function ChipBody({ item, on, T }) {
  return (
    <>
      {item.color && <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, flexShrink: 0 }} />}
      {item.name}
      <span style={{ fontSize: 10.5, fontWeight: 600, color: on ? '#3b82f6' : T.text3 }}>{item.count}</span>
    </>
  );
}

const Chevron = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Personal chip order — saved per user in this browser (like the column customizer), so each
// user arranges their own strip without affecting anyone else.
function orderStorageKey(orderKey) {
  let uid = 'anon';
  try { uid = JSON.parse(localStorage.getItem('user') || '{}').id || 'anon'; } catch { /* ignore */ }
  return `chipOrder:${orderKey}:${uid}`;
}
function readOrder(orderKey) {
  if (!orderKey) return [];
  try { const v = JSON.parse(localStorage.getItem(orderStorageKey(orderKey))); return Array.isArray(v) ? v.map(String) : []; } catch { return []; }
}

export default function ChipOverflowStrip({ items: rawItems, activeKey, onSelect, T, darkMode = false, orderKey }) {
  const boxRef = useRef(null);
  const measureRef = useRef(null);
  const moreBtnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [panelPos, setPanelPos] = useState(null);

  // ── Drag-to-reorder (enabled when `orderKey` is given). The first item ("All …") is pinned. ──
  const reorderable = !!orderKey;
  const [order, setOrder] = useState(() => readOrder(orderKey));
  const [dragKey, setDragKey] = useState(null);
  const [dropAt, setDropAt] = useState(null); // { key, after } — where the dragged chip would land; key '__more' = the "+N more" button
  const items = (() => {
    if (!reorderable || !order.length || rawItems.length < 2) return rawItems;
    const [pinned, ...rest] = rawItems;
    const pos = new Map(order.map((k, i) => [k, i]));
    // Known keys follow the saved order; new ones (e.g. a just-created group) keep their natural
    // order after them — sort is stable, so ties keep the incoming order.
    const sorted = [...rest].sort((a, b) => (pos.has(String(a.key)) ? pos.get(String(a.key)) : Infinity) - (pos.has(String(b.key)) ? pos.get(String(b.key)) : Infinity));
    return [pinned, ...sorted];
  })();
  const saveOrder = (keys) => {
    // Merge with keys not currently shown (e.g. groups with no tasks under this filter) so
    // their saved place isn't lost.
    const shown = new Set(keys);
    const merged = [...keys, ...order.filter(k => !shown.has(k))];
    setOrder(merged);
    try { localStorage.setItem(orderStorageKey(orderKey), JSON.stringify(merged)); } catch { /* ignore */ }
  };
  const resetOrder = () => {
    setOrder([]);
    try { localStorage.removeItem(orderStorageKey(orderKey)); } catch { /* ignore */ }
  };
  const moveChip = (fromKey, targetKey, after) => {
    const keys = items.slice(1).map(i => String(i.key));
    const from = keys.indexOf(String(fromKey));
    if (from < 0) return;
    keys.splice(from, 1);
    let to;
    if (targetKey === '__first') to = 0;
    else {
      const t = keys.indexOf(String(targetKey));
      if (t < 0) return;
      to = after ? t + 1 : t;
    }
    keys.splice(to, 0, String(fromKey));
    saveOrder(keys);
  };
  const dragProps = (item) => {
    if (!reorderable || item.key === rawItems[0]?.key) return {};
    return {
      draggable: true,
      onDragStart: (e) => {
        setDragKey(item.key);
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(item.key)); } catch { /* ignore */ }
      },
      onDragEnd: () => { setDragKey(null); setDropAt(null); },
    };
  };
  const dropProps = (item, axis) => {
    if (!reorderable) return {};
    const isPinned = item.key === rawItems[0]?.key;
    return {
      onDragOver: (e) => {
        if (dragKey == null) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (isPinned) { if (dropAt?.key !== '__first') setDropAt({ key: '__first', after: true }); return; }
        const r = e.currentTarget.getBoundingClientRect();
        const after = axis === 'y' ? e.clientY > r.top + r.height / 2 : e.clientX > r.left + r.width / 2;
        if (dropAt?.key !== item.key || dropAt?.after !== after) setDropAt({ key: item.key, after });
      },
      onDrop: (e) => {
        e.preventDefault();
        if (dragKey == null) return;
        if (isPinned) moveChip(dragKey, '__first', false);
        else if (item.key !== dragKey) {
          const r = e.currentTarget.getBoundingClientRect();
          const after = axis === 'y' ? e.clientY > r.top + r.height / 2 : e.clientX > r.left + r.width / 2;
          moveChip(dragKey, item.key, after);
        }
        setDragKey(null); setDropAt(null);
      },
    };
  };
  // Blue insertion marker on the side the chip will land.
  const dropMark = (item, axis) => {
    if (!dropAt || dragKey == null) return null;
    const isPinned = item.key === rawItems[0]?.key;
    if (isPinned && dropAt.key === '__first') return axis === 'y' ? 'inset 0 -2px 0 #3b82f6' : 'inset -3px 0 0 #3b82f6';
    if (dropAt.key !== item.key || item.key === dragKey) return null;
    if (axis === 'y') return dropAt.after ? 'inset 0 -2px 0 #3b82f6' : 'inset 0 2px 0 #3b82f6';
    return dropAt.after ? 'inset -3px 0 0 #3b82f6' : 'inset 3px 0 0 #3b82f6';
  };

  const [fit, setFit] = useState(items.length);

  // Signature of everything that changes chip widths — re-measure when it changes.
  const sig = items.map(i => `${i.key}|${i.name}|${i.count}`).join('~') + `|${activeKey}`;

  const measure = () => {
    const box = boxRef.current, m = measureRef.current;
    if (!box || !m) return;
    const avail = box.clientWidth;
    const chips = Array.from(m.children);
    const activeMoreEl = chips.pop(); // "more" button showing the active chip (used when it is hidden)
    const plusEl = chips.pop();       // "+NN more" button
    const widths = chips.map(c => c.getBoundingClientRect().width);
    const total = widths.reduce((a, w) => a + w, 0) + GAP * Math.max(0, widths.length - 1);
    if (total <= avail) { setFit(items.length); return; }
    const plusW = plusEl.getBoundingClientRect().width;
    const activeMoreW = activeMoreEl.getBoundingClientRect().width;
    const activeIdx = items.findIndex(i => i.key === activeKey);
    // Largest k such that the first k chips + the "more" button fit. The button is wider when
    // it has to show the active chip (i.e. when the active chip is among the hidden ones).
    let best = 1, used = 0;
    for (let k = 1; k < widths.length; k++) {
      used += (k > 1 ? GAP : 0) + widths[k - 1];
      const moreW = activeIdx >= k ? activeMoreW : plusW;
      if (used + GAP + moreW <= avail) best = k; else break;
    }
    setFit(best); // the first item ("All …") always stays visible
  };

  useLayoutEffect(measure, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!boxRef.current) return undefined;
    const onResize = () => measure();
    window.addEventListener('resize', onResize); // fallback for browsers without ResizeObserver
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (ro) ro.observe(boxRef.current);
    return () => { window.removeEventListener('resize', onResize); if (ro) ro.disconnect(); };
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = items.slice(0, fit);
  const hidden = items.slice(fit);
  const activeHidden = hidden.find(i => i.key === activeKey);

  // Dropdown: portalled to <body> (the strip sits inside overflow-clipped cards).
  const openPanel = () => {
    const r = moreBtnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 260;
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    setPanelPos({ top: r.bottom + 6, left, width });
    setQuery('');
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (moreBtnRef.current?.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-chip-overflow-panel]')) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = (e) => { if (!(e.target.closest && e.target.closest('[data-chip-overflow-panel]'))) setOpen(false); };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);
  // A filter change that empties the overflow (e.g. items shrink) closes the panel.
  useEffect(() => { if (open && hidden.length === 0) setOpen(false); }, [open, hidden.length]);

  const q = query.trim().toLowerCase();
  const shownHidden = q ? hidden.filter(i => String(i.name).toLowerCase().includes(q)) : hidden;

  return (
    <div ref={boxRef} style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: GAP, overflow: 'hidden' }}>
      {visible.map(item => {
        const on = activeKey === item.key;
        return (
          <button key={item.key} type="button" onClick={() => onSelect(item.key)}
            title={reorderable && item.key !== rawItems[0]?.key ? 'Click to filter · drag to reorder' : undefined}
            {...dragProps(item)} {...dropProps(item, 'x')}
            style={{ ...chipStyle(on, T, darkMode), opacity: dragKey === item.key ? 0.4 : 1, boxShadow: dropMark(item, 'x') || 'none' }}>
            <ChipBody item={item} on={on} T={T} />
          </button>
        );
      })}

      {hidden.length > 0 && (
        <button ref={moreBtnRef} type="button" onClick={() => (open ? setOpen(false) : openPanel())}
          title={`${hidden.length} more`}
          {...(reorderable ? {
            // Dropping a visible chip here sends it to the start of the "more" list.
            onDragOver: (e) => { if (dragKey == null) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropAt?.key !== '__more') setDropAt({ key: '__more', after: false }); },
            onDrop: (e) => {
              e.preventDefault();
              if (dragKey != null && hidden.length && hidden[0].key !== dragKey) moveChip(dragKey, hidden[0].key, false);
              setDragKey(null); setDropAt(null);
            },
          } : {})}
          style={{ ...chipStyle(!!activeHidden, T, darkMode), background: activeHidden ? chipStyle(true, T, darkMode).background : (open ? T.bg3 : 'transparent'),
            boxShadow: dropAt?.key === '__more' && dragKey != null ? 'inset 3px 0 0 #3b82f6' : 'none' }}>
          {activeHidden ? <ChipBody item={activeHidden} on T={T} /> : <span>+{hidden.length} more</span>}
          {activeHidden && hidden.length > 1 && <span style={{ fontSize: 10.5, color: '#3b82f6' }}>· +{hidden.length - 1}</span>}
          <Chevron />
        </button>
      )}

      {/* Off-screen measuring row: every chip at its natural width + a widest-case "more" button */}
      <div ref={measureRef} aria-hidden="true"
        style={{ position: 'absolute', left: 0, top: 0, visibility: 'hidden', pointerEvents: 'none', display: 'flex', gap: GAP, whiteSpace: 'nowrap' }}>
        {items.map(item => (
          <span key={item.key} style={chipStyle(activeKey === item.key, T, darkMode)}>
            <ChipBody item={item} on={activeKey === item.key} T={T} />
          </span>
        ))}
        <span style={chipStyle(false, T, darkMode)}><span>+{Math.max(items.length - 1, 9)} more</span><Chevron /></span>
        <span style={chipStyle(true, T, darkMode)}>
          {(() => { const a = items.find(i => i.key === activeKey); return a ? <ChipBody item={a} on T={T} /> : null; })()}
          <span style={{ fontSize: 10.5 }}>· +{Math.max(items.length - 1, 9)}</span>
          <Chevron />
        </span>
      </div>

      {open && panelPos && ReactDOM.createPortal(
        <div data-chip-overflow-panel="" style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, width: panelPos.width, zIndex: 100000,
          background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 12, padding: 6,
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.15)' }}>
          {hidden.length > 5 && (
            <input autoFocus type="text" placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: 4, border: `1px solid ${T.inputBorder || T.border2}`, borderRadius: 8, padding: '7px 10px', fontSize: 12.5, outline: 'none', background: T.inputBg || T.bg2, color: T.text }} />
          )}
          <div style={{ maxHeight: 280, overflowY: 'auto', scrollbarWidth: 'thin' }}>
            {shownHidden.length === 0 ? (
              <p style={{ fontSize: 12, color: T.text3, textAlign: 'center', padding: '12px 0', margin: 0 }}>No match</p>
            ) : shownHidden.map(item => {
              const on = activeKey === item.key;
              return (
                <button key={item.key} type="button" onClick={() => { onSelect(item.key); setOpen(false); }}
                  {...(q ? {} : { ...dragProps(item), ...dropProps(item, 'y') })}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', padding: '8px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                    opacity: dragKey === item.key ? 0.4 : 1, boxShadow: (!q && dropMark(item, 'y')) || 'none',
                    background: on ? (darkMode ? 'rgba(59,130,246,0.15)' : '#eff6ff') : 'transparent', color: on ? '#3b82f6' : T.text, fontSize: 12.5, fontWeight: on ? 600 : 500 }}
                  onMouseEnter={e => { if (!on) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
                  {reorderable && !q && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill={T.text3} style={{ flexShrink: 0, cursor: 'grab' }} aria-hidden="true">
                      <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
                    </svg>
                  )}
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color || 'transparent', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: on ? '#3b82f6' : T.text3 }}>{item.count}</span>
                </button>
              );
            })}
          </div>
          {reorderable && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderTop: `1px solid ${T.border}`, marginTop: 4, padding: '6px 6px 2px' }}>
              <span style={{ fontSize: 11, color: T.text3 }}>Drag chips to reorder</span>
              {order.length > 0 && (
                <button type="button" onClick={() => { resetOrder(); setOpen(false); }}
                  style={{ border: 'none', background: 'none', color: '#3b82f6', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                  Reset order
                </button>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
